/**
 * BdrDaily.tsx
 *
 * BDR Daily Surface V2 — Mi6 Attilius Intelligence Platform
 * Route: /#/today (role-routed for BDR)
 *
 * W7-CadenceTodoFeed: Primary cadence queue now sourced from
 * /api/cadence/queue/today (cadence_enrollments JOIN cadence_prospects
 * JOIN cadence_programs). The old hunt_items "Suggested touches" block
 * is preserved as a secondary "Hunt list (unenrolled prospects)" section.
 *
 * Canon authority:
 *   - bdr-daily-surface-v2-spec.md (locked 2026-05-28)
 *   - Brand palette: black #0B0B0F, red #DC2626, soft white #FAFAF7
 *   - No emojis. No exclamation points. M-PACT hyphenated (not surfaced here).
 *
 * Data hooks used:
 *   - useCadenceTodayQueue() — PRIMARY: live cadence queue from cadence_enrollments
 *   - useAccounts() / useHuntForAccounts() — SECONDARY: hunt list (unenrolled)
 *   - getSession() — to identify current BDR user
 */

import { useState, useMemo, useEffect } from "react";
import { Link } from "wouter";
import { getHealthColor } from "@shared/schema";
import {
  useAccounts,
  useHuntForAccounts,
  type ApiAccount,
  type HuntItem,
  LoadingStrip,
} from "@/pages/dashboards/live-data";
import { getSession, supabase } from "@/lib/session";
import OperatorShell from "@/components/operator/OperatorShell";
import { computeStartHereBdr } from "@/lib/daily/startHere";
import {
  useCadenceTodayQueue,
  type CadenceQueueItem,
  type CompleteStepOutcome,
} from "@/hooks/useCadenceTodayQueue";
import { AIP_VERSION } from "@/version";

// ─── Canon Brand Constants ──────────────────────────────────────────────────

const AIP_BG = "#FAFAF7";
const MI6_BLACK = "#0B0B0F";
const MI6_RED = "#DC2626";
const SLATE = "#6B6B70";
const BORDER = "#E5E5E7";
const GREEN_DARK = "#16A34A";
const AMBER = "#EAB308";

// ─── Cadence Step Types — Locked Canon Sequence ────────────────────────────

export type CadenceStepType =
  | "email_1"
  | "call_1_no_vm"
  | "linkedin_value"
  | "email_2"
  | "call_2_with_vm"
  | "escalation_review";

export const STEP_LABELS: Record<CadenceStepType, string> = {
  email_1:           "Email #1",
  call_1_no_vm:      "Call #1 — no voicemail",
  linkedin_value:    "LinkedIn value engagement",
  email_2:           "Email #2",
  call_2_with_vm:    "Call #2 — with voicemail",
  escalation_review: "Marketing escalation review",
};

export const STEP_ACTION_LABELS: Record<CadenceStepType, string> = {
  email_1:           "Send email",
  call_1_no_vm:      "Open dialer",
  linkedin_value:    "Open LinkedIn",
  email_2:           "Send email",
  call_2_with_vm:    "Open dialer",
  escalation_review: "Recommend for marketing review",
};

export const STEP_NUMBERS: Record<CadenceStepType, number> = {
  email_1:           1,
  call_1_no_vm:      2,
  linkedin_value:    3,
  email_2:           4,
  call_2_with_vm:    5,
  escalation_review: 6,
};

// ─── Data Types (legacy hunt queue) ────────────────────────────────────────

export interface CadenceQueueRow {
  id: string;
  contactName: string;
  contactTitle: string;
  accountName: string;
  accountType: "Auto OEM" | "Tier 1" | "Tier 2";
  stepType: CadenceStepType;
  dueDate: string;
  healthScore: number | null;
  linkedinUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  isOverdue: boolean;
  hasMarketingSignal: boolean;
  marketingSignalLabel?: string;
  escalationStatus?: "pending_review" | null;
}

export interface ConversionRatioData {
  label: string;
  personal: number;
  teamAvg: number;
}

export interface ActivityTickerItem {
  time: string;
  channel: CadenceStepType;
  contactName: string;
  accountName: string;
}

export interface CadenceHealthData {
  compliancePct: number;
  forward: number;
  atGate: number;
  backward: number;
}

// ─── Hunt queue helpers ────────────────────────────────────────────────────

function inferStepType(item: HuntItem): CadenceStepType {
  const key = (item.itemKey ?? "").toLowerCase();
  if (key.includes("email_2") || key.includes("email2")) return "email_2";
  if (key.includes("call_2") || key.includes("call2") || key.includes("vm")) return "call_2_with_vm";
  if (key.includes("linkedin")) return "linkedin_value";
  if (key.includes("call_1") || key.includes("call1") || key.includes("call")) return "call_1_no_vm";
  if (key.includes("escalat") || key.includes("step6") || key.includes("step_6")) return "escalation_review";
  return "email_1";
}

function inferAccountType(account?: ApiAccount): "Auto OEM" | "Tier 1" | "Tier 2" {
  const industry = (account?.industry ?? "").toLowerCase();
  if (industry.includes("oem")) return "Auto OEM";
  if (industry.includes("tier 1") || industry.includes("tier1")) return "Tier 1";
  return "Tier 2";
}

function buildQueueFromHunt(huntItems: HuntItem[], accounts: ApiAccount[]): CadenceQueueRow[] {
  const acctMap = new Map(accounts.map((a) => [a.id, a]));
  const now = Date.now();
  return huntItems
    .filter((h) => h.status === "open" || h.status === "chasing")
    .map((h): CadenceQueueRow => {
      const acct = acctMap.get(h.accountId);
      const dueMs = h.dueDate ? new Date(h.dueDate).getTime() : now;
      const isOverdue = dueMs < now;
      const stepType = inferStepType(h);
      return {
        id: String(h.id),
        contactName: h.label ?? `Contact (account ${h.accountId})`,
        contactTitle: "",
        accountName: acct?.name ?? `Account #${h.accountId}`,
        accountType: inferAccountType(acct),
        stepType,
        dueDate: h.dueDate ?? new Date().toISOString().split("T")[0],
        healthScore: acct?.score ?? null,
        isOverdue,
        hasMarketingSignal: false,
        escalationStatus: null,
      };
    })
    .sort((a, b) => {
      if (a.stepType === "escalation_review" && b.stepType !== "escalation_review") return -1;
      if (b.stepType === "escalation_review" && a.stepType !== "escalation_review") return 1;
      if (a.isOverdue && !b.isOverdue) return -1;
      if (b.isOverdue && !a.isOverdue) return 1;
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    })
    .slice(0, 10);
}

// ─── UI Components ─────────────────────────────────────────────────────────

function HealthDot({ score }: { score: number | null }) {
  if (score === null) return <span className="inline-block w-2.5 h-2.5 rounded-full bg-neutral-300" title="Not yet scored" />;
  const color = getHealthColor(score);
  return <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} title={`Health score: ${score}`} />;
}

function DuePill({ dueDate, isOverdue }: { dueDate: string; isOverdue: boolean }) {
  const formatted = new Date(dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
      style={{ backgroundColor: isOverdue ? "#FEE2E2" : "#F3F4F6", color: isOverdue ? MI6_RED : MI6_BLACK, border: `1px solid ${isOverdue ? "#FECACA" : BORDER}` }}>
      {isOverdue ? "Overdue · " : ""}{formatted}
    </span>
  );
}

function DueBadge({ dueAt, daysOverdue }: { dueAt: string | null; daysOverdue: number }) {
  if (!dueAt) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: "#F3F4F6", color: SLATE, border: `1px solid ${BORDER}` }}>No due date</span>;
  const formatted = new Date(dueAt).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const todayStr = new Date().toISOString().slice(0, 10);
  const isToday = dueAt.slice(0, 10) === todayStr;
  if (daysOverdue === 0) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: "#D1FAE5", color: GREEN_DARK, border: "1px solid #6EE7B7" }}>{isToday ? "Due today" : formatted}</span>;
  if (daysOverdue <= 3) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}>{daysOverdue}d overdue</span>;
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: "#FEE2E2", color: MI6_RED, border: "1px solid #FECACA" }}>{daysOverdue}d overdue</span>;
}

function DotLadder({ completedSteps, total }: { completedSteps: number; total: number }) {
  return (
    <div className="flex items-center gap-0.5 mt-1">
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: i < completedSteps ? MI6_RED : "#D1D5DB" }} />
      ))}
    </div>
  );
}

// ─── Live Cadence Queue Row ─────────────────────────────────────────────────

const OUTCOMES: { value: CompleteStepOutcome; label: string }[] = [
  { value: "connected",      label: "Connected" },
  { value: "no_answer",      label: "No answer" },
  { value: "bounced",        label: "Bounced" },
  { value: "booked_meeting", label: "Booked meeting" },
  { value: "other",          label: "Other" },
];

function LiveQueueRow({ item, onMarkDone, isCompleting }: {
  item: CadenceQueueItem;
  onMarkDone: (enrollmentId: string, outcome: CompleteStepOutcome | null, notes?: string) => void;
  isCompleting: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [outcome, setOutcome] = useState<CompleteStepOutcome | "">("");
  const stepsDone = item.currentStepNumber - 1;

  return (
    <tr className="border-b hover:bg-neutral-50 transition-colors"
      style={{ borderLeftWidth: "3px", borderLeftStyle: "solid", borderLeftColor: item.daysOverdue > 3 ? MI6_RED : item.daysOverdue > 0 ? AMBER : "transparent", borderBottomColor: BORDER }}>
      <td className="px-4 py-3 align-top" style={{ width: "25%" }}>
        <div className="text-sm font-bold leading-tight" style={{ color: MI6_BLACK }}>{item.prospectName}</div>
        {item.company && <div className="text-[11px] mt-0.5 font-medium" style={{ color: MI6_BLACK }}>{item.company}</div>}
        {item.title && <div className="text-[11px] mt-0.5" style={{ color: SLATE }}>{item.title}</div>}
        {item.marketingHookKind && <div className="text-[9px] mt-1 font-medium" style={{ color: AMBER }}>{item.marketingHookKind}</div>}
      </td>
      <td className="px-4 py-3 align-top" style={{ width: "30%" }}>
        <div className="text-[11px] font-semibold" style={{ color: SLATE }}>
          Step {item.currentStepNumber} of {item.totalSteps} &mdash; {item.actionKind}
        </div>
        <div className="text-[12px] mt-0.5 font-medium" style={{ color: MI6_BLACK }}>{item.actionTitle}</div>
        <DotLadder completedSteps={stepsDone} total={item.totalSteps} />
        {item.actionScript && (
          <div className="mt-1">
            <button type="button" className="text-[10px] underline underline-offset-2 hover:no-underline" style={{ color: SLATE }} onClick={() => setExpanded((v) => !v)}>
              {expanded ? "Hide script" : "Show script"}
            </button>
            {expanded && <div className="text-[11px] mt-1 leading-relaxed p-2 rounded" style={{ background: AIP_BG, border: `1px solid ${BORDER}`, color: SLATE }}>{item.actionScript}</div>}
          </div>
        )}
      </td>
      <td className="px-4 py-3 align-top" style={{ width: "15%" }}>
        <DueBadge dueAt={item.dueAt} daysOverdue={item.daysOverdue} />
      </td>
      <td className="px-4 py-3 align-top text-right" style={{ width: "30%" }}>
        <div className="flex items-center justify-end gap-2">
          <select value={outcome} onChange={(e) => setOutcome(e.target.value as CompleteStepOutcome | "")}
            className="text-[11px] rounded px-2 py-1 border" style={{ borderColor: BORDER, color: MI6_BLACK, background: "#FFFFFF" }} disabled={isCompleting}>
            <option value="">Outcome...</option>
            {OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button type="button" disabled={isCompleting}
            onClick={() => onMarkDone(item.enrollmentId, (outcome as CompleteStepOutcome) || null)}
            className="inline-flex items-center px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-[0.12em] text-white transition-opacity hover:opacity-80 disabled:opacity-40 disabled:cursor-wait"
            style={{ background: MI6_RED }}>
            {isCompleting ? "Saving..." : "Mark done"}
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Legacy hunt queue row ─────────────────────────────────────────────────

function handleStepAction(row: CadenceQueueRow, onEscalationOpen: (row: CadenceQueueRow) => void): void {
  switch (row.stepType) {
    case "email_1": case "email_2":
      if (row.contactEmail) window.open(`mailto:${row.contactEmail}?subject=Following up — ${row.accountName}`, "_blank");
      break;
    case "call_1_no_vm": case "call_2_with_vm":
      if (row.contactPhone) window.open(`tel:${row.contactPhone}`, "_blank");
      break;
    case "linkedin_value":
      if (row.linkedinUrl) window.open(row.linkedinUrl, "_blank");
      break;
    case "escalation_review":
      onEscalationOpen(row);
      break;
  }
}

function MarketingEscalationModal({ row, onClose }: { row: CadenceQueueRow; onClose: () => void }) {
  const [recommendation, setRecommendation] = useState<"targeted_marketing" | "long_form_nurture" | "disqualify" | null>(null);
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit() {
    if (!recommendation) return;
    console.log("Escalation recommendation submitted:", { row, recommendation, notes });
    setSubmitted(true);
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-5" style={{ border: `1px solid ${BORDER}` }}>
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: SLATE }}>Step 6 — Marketing escalation review</div>
          <h2 className="text-lg font-bold" style={{ color: MI6_BLACK }}>Recommend for targeted marketing review</h2>
          <p className="text-[12px] mt-1 leading-snug" style={{ color: SLATE }}>This prospect has completed the 6-touch cold outbound sequence without responding. The next step is a human decision — not an automated action.</p>
        </div>
        <div className="rounded-lg p-3" style={{ background: AIP_BG, border: `1px solid ${BORDER}` }}>
          <div className="text-sm font-bold" style={{ color: MI6_BLACK }}>{row.contactName}</div>
          <div className="text-[11px]" style={{ color: SLATE }}>{row.contactTitle ? `${row.contactTitle} · ` : ""}{row.accountName} · {row.accountType}</div>
        </div>
        {!submitted ? (
          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: SLATE }}>Suggested path — select one</div>
            {([
              { value: "targeted_marketing" as const, label: "Promote to targeted marketing", sub: "Custom direct mail, executive gift, sponsored event invite, or custom video." },
              { value: "long_form_nurture" as const, label: "Move to long-form nurture", sub: "Marketing drip sequence. No immediate BDR action." },
              { value: "disqualify" as const, label: "Disqualify — log reason and close out", sub: "Remove from active cadence. Reason is logged for reporting." },
            ]).map((opt) => (
              <label key={opt.value} className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                style={{ background: recommendation === opt.value ? "#FEF2F2" : AIP_BG, border: `1px solid ${recommendation === opt.value ? "#FECACA" : BORDER}` }}>
                <input type="radio" name="escalation_recommendation" value={opt.value} checked={recommendation === opt.value} onChange={() => setRecommendation(opt.value)} className="mt-0.5 flex-shrink-0" style={{ accentColor: MI6_RED }} />
                <div>
                  <div className="text-sm font-semibold" style={{ color: MI6_BLACK }}>{opt.label}</div>
                  <div className="text-[11px] mt-0.5 leading-snug" style={{ color: SLATE }}>{opt.sub}</div>
                </div>
              </label>
            ))}
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] font-bold block mb-1" style={{ color: SLATE }}>Context for manager or marketing team (optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 280))} rows={3} className="w-full rounded-lg text-sm resize-none px-3 py-2" style={{ border: `1px solid ${BORDER}`, color: MI6_BLACK, background: "#FFFFFF" }} maxLength={280} />
            </div>
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={onClose} className="text-sm font-medium px-4 py-2 rounded-lg" style={{ color: SLATE }}>Cancel</button>
              <button type="button" onClick={handleSubmit} disabled={!recommendation} className="text-sm font-bold px-5 py-2 rounded-lg text-white"
                style={{ background: recommendation ? MI6_RED : "#D1D5DB", cursor: recommendation ? "pointer" : "not-allowed" }}>
                Send recommendation to manager
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center space-y-3 py-4">
            <div className="text-2xl">&#10003;</div>
            <div className="text-base font-bold" style={{ color: MI6_BLACK }}>Recommendation submitted</div>
            <button type="button" onClick={onClose} className="text-sm font-bold px-5 py-2 rounded-lg text-white" style={{ background: MI6_BLACK }}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

function QueueRow({ row, onEscalationOpen }: { row: CadenceQueueRow; onEscalationOpen: (row: CadenceQueueRow) => void }) {
  const stepNum = STEP_NUMBERS[row.stepType];
  const actionLabel = STEP_ACTION_LABELS[row.stepType];
  const stepLabel = STEP_LABELS[row.stepType];
  const isStep6 = row.stepType === "escalation_review";
  let leftBorder = "transparent";
  if (row.isOverdue) leftBorder = MI6_RED;
  else if (row.hasMarketingSignal) leftBorder = AMBER;
  else if (isStep6) leftBorder = MI6_BLACK;

  return (
    <tr className="border-b hover:bg-neutral-50 transition-colors"
      style={{ borderLeftWidth: "3px", borderLeftStyle: "solid", borderLeftColor: leftBorder, borderBottomColor: BORDER, background: isStep6 && row.escalationStatus !== "pending_review" ? "#FEF9F0" : undefined }}>
      <td className="px-4 py-3 align-top" style={{ width: "20%" }}>
        <div className="text-sm font-semibold leading-tight" style={{ color: MI6_BLACK }}>{row.contactName}</div>
        {row.contactTitle && <div className="text-[11px] mt-0.5" style={{ color: SLATE }}>{row.contactTitle}</div>}
      </td>
      <td className="px-4 py-3 align-top" style={{ width: "18%" }}>
        <div className="text-sm font-medium" style={{ color: MI6_BLACK }}>{row.accountName}</div>
        <span className="inline-block text-[9px] uppercase tracking-[0.12em] font-bold px-1.5 py-0.5 rounded mt-1" style={{ background: "#F3F4F6", color: SLATE }}>{row.accountType}</span>
      </td>
      <td className="px-4 py-3 align-top" style={{ width: "20%" }}>
        <div className="text-[11px] font-semibold" style={{ color: isStep6 ? MI6_BLACK : SLATE }}>Step {stepNum} of 6</div>
        <div className="text-[11px] mt-0.5" style={{ color: MI6_BLACK }}>{stepLabel}</div>
        <DotLadder completedSteps={stepNum - 1} total={6} />
      </td>
      <td className="px-4 py-3 align-top" style={{ width: "12%" }}>
        <DuePill dueDate={row.dueDate} isOverdue={row.isOverdue} />
      </td>
      <td className="px-3 py-3 align-top text-center" style={{ width: "6%" }}>
        <HealthDot score={row.healthScore} />
      </td>
      <td className="px-4 py-3 align-top" style={{ width: "18%" }}>
        {row.escalationStatus === "pending_review" ? (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-[0.12em]" style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}>Recommendation pending</div>
        ) : (
          <button type="button" onClick={() => handleStepAction(row, onEscalationOpen)}
            disabled={row.stepType === "linkedin_value" && !row.linkedinUrl}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-[0.12em] text-white transition-opacity hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: isStep6 ? MI6_BLACK : MI6_RED }}>
            {actionLabel}
          </button>
        )}
      </td>
      <td className="px-4 py-3 align-top" style={{ width: "6%" }}>
        <button type="button" className="text-[10px] font-medium underline underline-offset-2 hover:no-underline" style={{ color: SLATE }} onClick={() => console.log("Log touch:", row)}>Log</button>
      </td>
    </tr>
  );
}

// ─── Conversion Ratio Strip ────────────────────────────────────────────────

const STUB_CONVERSION: ConversionRatioData[] = [
  { label: "Dials → Connects",  personal: 18, teamAvg: 20 },
  { label: "Connects → Convos", personal: 44, teamAvg: 42 },
  { label: "Convos → Meetings", personal: 24, teamAvg: 28 },
  { label: "Meetings → Held",   personal: 71, teamAvg: 76 },
  { label: "Held → Handoff AE", personal: 82, teamAvg: 80 },
];

function ConversionRatioStrip({ data }: { data: ConversionRatioData[] }) {
  return (
    <div className="rounded-xl p-5" style={{ background: "#FFFFFF", border: `1px solid ${BORDER}` }}>
      <div className="text-[9px] uppercase tracking-[0.2em] font-bold mb-4" style={{ color: SLATE }}>Your conversion ratios · last 30 days</div>
      <div className="space-y-3">
        {data.map((item) => {
          const below = item.personal < item.teamAvg - 10;
          const above = item.personal >= item.teamAvg;
          return (
            <div key={item.label} className="flex items-center gap-3">
              <div className="text-[12px] flex-1" style={{ color: MI6_BLACK }}>{item.label}</div>
              <div className="text-[15px] font-bold tabular-nums" style={{ color: below ? MI6_RED : above ? GREEN_DARK : MI6_BLACK }}>{item.personal}%</div>
              <div className="text-[11px]" style={{ color: SLATE }}>Team avg: {item.teamAvg}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Cadence Health Panel ─────────────────────────────────────────────────

function CadenceHealthPanel({ data }: { data: CadenceHealthData }) {
  const healthColor = getHealthColor(data.compliancePct);
  return (
    <div className="rounded-xl p-5 space-y-4" style={{ background: "#FFFFFF", border: `1px solid ${BORDER}` }}>
      <div className="text-[9px] uppercase tracking-[0.2em] font-bold" style={{ color: SLATE }}>Cadence health · today</div>
      <div className="text-4xl font-black tabular-nums leading-none" style={{ color: healthColor }}>{data.compliancePct}%</div>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[
          { label: "Forward",        value: data.forward,  color: GREEN_DARK },
          { label: "At Checkpoint",  value: data.atGate,   color: AMBER },
          { label: "Backward",       value: data.backward, color: MI6_RED },
        ].map((stat) => (
          <div key={stat.label}>
            <div className="text-xl font-black tabular-nums" style={{ color: stat.value > 0 ? stat.color : MI6_BLACK }}>{stat.value}</div>
            <div className="text-[9px] mt-0.5" style={{ color: SLATE }}>{stat.label}</div>
          </div>
        ))}
      </div>
      <div className="pt-2 border-t" style={{ borderColor: BORDER }}>
        <Link href="/cadence/reporting" className="text-[11px] font-semibold hover:opacity-70" style={{ color: MI6_BLACK }}>View reporting &rarr;</Link>
      </div>
    </div>
  );
}

function ActivityTicker({ items }: { items: ActivityTickerItem[] }) {
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: "#FFFFFF", border: `1px solid ${BORDER}` }}>
      <div className="px-4 py-3 border-b" style={{ borderColor: BORDER }}>
        <div className="text-[9px] uppercase tracking-[0.2em] font-bold" style={{ color: SLATE }}>Activity today</div>
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-4 text-[12px]" style={{ color: SLATE }}>No touches logged yet today — your queue is ready.</div>
      ) : (
        <div className="divide-y" style={{ borderColor: BORDER }}>
          {items.map((item, i) => (
            <div key={i} className="px-4 py-2.5">
              <div className="text-[11px] font-medium" style={{ color: MI6_BLACK }}>{item.contactName} · {item.accountName}</div>
              <div className="text-[10px]" style={{ color: SLATE }}>{item.time} · {STEP_LABELS[item.channel]}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Page Header ──────────────────────────────────────────────────────────

function BdrPageHeader({ repName, daysLeftInMonth, month, dateLabel }: {
  repName: string; daysLeftInMonth: number; month: string; dateLabel: string;
}) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl font-semibold" style={{ color: MI6_BLACK }}>{repName}</span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] text-white" style={{ background: MI6_RED }}>BDR</span>
      </div>
      <div className="text-[13px]" style={{ color: SLATE }}>{dateLabel}</div>
      <div className="text-[13px] mt-0.5" style={{ color: SLATE }}>{daysLeftInMonth} days left in {month} — suggested activity pace is on track.</div>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────

function BdrDailySkeleton() {
  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pt-2">
      <div className="rounded-lg p-4" style={{ background: "#FFFFFF", borderLeft: `4px solid ${MI6_RED}`, border: `1px solid ${BORDER}` }}>
        <p className="text-xs uppercase tracking-wide font-bold mb-2" style={{ color: MI6_RED }}>Start here</p>
        <LoadingStrip height={24} />
      </div>
      <LoadingStrip height={48} />
      <div>
        <p className="text-xs uppercase tracking-wide mb-3" style={{ color: SLATE }}>Cadence queue</p>
        <LoadingStrip height={320} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide mb-3" style={{ color: SLATE }}>Conversion ratios — last 30 days</p>
        <LoadingStrip height={140} />
      </div>
    </div>
  );
}

// ─── Start Here Banner ────────────────────────────────────────────────────

function BdrStartHereBanner({ sentence, sectionId, linkText }: {
  sentence: string; sectionId: string; linkText: string;
}) {
  function scrollTo() {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  return (
    <div className="rounded-lg p-4 flex items-start justify-between gap-4"
      style={{ background: "#FFFFFF", borderLeft: `4px solid ${MI6_RED}`, border: `1px solid ${BORDER}` }}
      data-testid="start-here">
      <div>
        <p className="text-xs uppercase tracking-wide font-bold mb-1" style={{ color: MI6_RED }}>Start here</p>
        <p className="text-sm font-medium" style={{ color: MI6_BLACK }}>{sentence}</p>
      </div>
      <button type="button" onClick={scrollTo} className="text-xs font-semibold whitespace-nowrap underline underline-offset-2 hover:no-underline flex-shrink-0 mt-1" style={{ color: MI6_RED }}>
        {linkText} &darr;
      </button>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────

function BdrEmptyState() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" style={{ background: AIP_BG }}>
      <p className="text-lg font-semibold mb-2" style={{ color: MI6_BLACK }}>No prospects in your hunt list.</p>
      <p className="text-sm max-w-sm" style={{ color: SLATE }}>
        Upload contacts at{" "}
        <Link href="/upload-contacts" style={{ color: MI6_RED }}>/#/upload-contacts</Link>.
      </p>
    </div>
  );
}

// ─── Manager Coaching Section ─────────────────────────────────────────────

interface CoachingNoteBdr { id: string; body: string; created_at: string; }

function ManagerCoachingSection({ repUserId }: { repUserId: string }) {
  const [notes, setNotes] = useState<CoachingNoteBdr[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!repUserId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase().from("coaching_notes").select("*").eq("rep_user_id", repUserId).eq("visibility", "rep_visible").order("created_at", { ascending: false });
        if (!cancelled && !error && data) setNotes(data as CoachingNoteBdr[]);
      } catch { /* ignore */ } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [repUserId]);

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: "#FFFFFF", border: `1px solid ${BORDER}` }}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full px-5 py-3 border-b flex items-center justify-between text-left" style={{ borderColor: BORDER }} aria-expanded={open}>
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] font-bold" style={{ color: SLATE }}>Coaching from your manager</div>
          <div className="text-base font-bold mt-0.5" style={{ color: MI6_BLACK }}>
            {loading ? "Loading..." : notes.length === 0 ? "No coaching notes yet." : `${notes.length} note${notes.length !== 1 ? "s" : ""} from your manager`}
          </div>
        </div>
        <span className="text-[11px] font-medium" style={{ color: SLATE }}>{open ? "Collapse" : "Expand"}</span>
      </button>
      {open && (
        <div className="px-5 py-4 space-y-3">
          {loading && <p className="text-[12px]" style={{ color: SLATE }}>Loading coaching notes...</p>}
          {!loading && notes.length === 0 && <p className="text-[12px]" style={{ color: SLATE }}>No coaching notes yet.</p>}
          {!loading && notes.map((n) => (
            <div key={n.id} className="rounded-lg p-4" style={{ background: AIP_BG, border: `1px solid ${BORDER}` }}>
              <p className="text-sm leading-relaxed" style={{ color: MI6_BLACK }}>{n.body}</p>
              <p className="text-[10px] mt-2" style={{ color: SLATE }}>{new Date(n.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Derived cadence health ────────────────────────────────────────────────

function deriveCadenceHealth(queue: CadenceQueueItem[]): CadenceHealthData {
  if (queue.length === 0) return { compliancePct: 0, forward: 0, atGate: 0, backward: 0 };
  const forward = queue.filter((r) => r.daysOverdue === 0).length;
  const backward = queue.filter((r) => r.daysOverdue > 0).length;
  const compliancePct = Math.round((forward / queue.length) * 100);
  return { compliancePct, forward, atGate: 0, backward };
}

function daysLeftInCurrentMonth(): number {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() - now.getDate();
}

// ─── Main Component ────────────────────────────────────────────────────────

export default function BdrDaily() {
  const [escalationRow, setEscalationRow] = useState<CadenceQueueRow | null>(null);
  const [showMoreHunt, setShowMoreHunt] = useState(false);

  const session = getSession();
  const { data: accounts, loading: acctLoading } = useAccounts();
  const accountIds = useMemo(() => (accounts ?? []).map((a) => a.id), [accounts]);
  const { data: huntItems, loading: huntLoading } = useHuntForAccounts(accountIds);

  // PRIMARY: live cadence queue
  const { queue: cadenceQueue, loading: cadenceLoading, error: cadenceError, markStepDone, completing } = useCadenceTodayQueue();

  // SECONDARY: hunt list (unenrolled prospects)
  const huntQueue = useMemo(() => {
    if (!huntItems || !accounts) return [];
    return buildQueueFromHunt(huntItems, accounts);
  }, [huntItems, accounts]);

  const cadenceHealth = useMemo(() => deriveCadenceHealth(cadenceQueue), [cadenceQueue]);

  const startHere = useMemo(
    () => computeStartHereBdr(huntItems ?? [], accounts ?? [], cadenceQueue),
    [huntItems, accounts, cadenceQueue]
  );

  const loading = acctLoading || huntLoading || cadenceLoading;
  const now = new Date();
  const monthLabel = now.toLocaleDateString("en-US", { month: "long" });
  const fullDateLabel = now.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const daysLeft = daysLeftInCurrentMonth();
  const repName = session?.name ?? "BDR";
  const repUserId = (session as any)?.id ?? "";

  const displayHuntQueue = showMoreHunt ? huntQueue : huntQueue.slice(0, 10);
  const hasMoreHunt = huntQueue.length > 10 && !showMoreHunt;
  const activityTicker: ActivityTickerItem[] = [];
  const isEmpty = !loading && cadenceQueue.length === 0 && (!huntItems || huntItems.length === 0) && (!accounts || accounts.length === 0);

  return (
    <OperatorShell breadcrumbs={[{ label: "Daily", href: "/today" }]} pageTitle={`${repName} — Daily View`} pageMeta={`BDR · ${fullDateLabel}`} showContextTabs={false}>
      {loading && <BdrDailySkeleton />}

      {isEmpty && (
        <div>
          <div className="max-w-[1400px] mx-auto pt-4">
            <BdrStartHereBanner sentence={startHere.sentence} sectionId={startHere.sectionId} linkText={startHere.linkText} />
          </div>
          <BdrEmptyState />
        </div>
      )}

      {!loading && !isEmpty && (
        <div className="min-h-[60vh]" style={{ background: AIP_BG }}>
          <div className="max-w-[1400px] mx-auto pt-4">
            <BdrStartHereBanner sentence={startHere.sentence} sectionId={startHere.sectionId} linkText={startHere.linkText} />
          </div>
          <div className="max-w-[1400px] mx-auto pt-4">
            <nav className="text-[11px]" style={{ color: SLATE }}>
              <Link href="/dashboards" style={{ color: SLATE }}>Dashboards</Link>
              <span className="mx-1">›</span>
              <span style={{ color: MI6_BLACK }}>BDR Daily Surface</span>
            </nav>
          </div>
          <div className="max-w-[1400px] mx-auto pt-4">
            <BdrPageHeader repName={repName} daysLeftInMonth={daysLeft} month={monthLabel} dateLabel={fullDateLabel} />
          </div>
          <div className="max-w-[1400px] mx-auto px-6 pb-10">
            <div className="flex gap-6">
              <div className="flex-1 min-w-0 space-y-6">

                {/* PRIMARY: Live Cadence Queue */}
                <div id="cadence-queue" className="rounded-xl overflow-hidden" style={{ background: "#FFFFFF", border: `1px solid ${BORDER}` }}>
                  <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: BORDER }}>
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] font-bold" style={{ color: SLATE }}>Cadence queue</div>
                      <div className="text-base font-bold mt-0.5" style={{ color: MI6_BLACK }}>
                        {cadenceQueue.length > 0 ? `${cadenceQueue.length} step${cadenceQueue.length !== 1 ? "s" : ""} due today` : "Cadence queue"}
                      </div>
                    </div>
                    <Link href="/cadence/bdr" className="text-[11px] font-bold uppercase tracking-wide hover:opacity-70" style={{ color: MI6_RED }}>Full queue →</Link>
                  </div>
                  {cadenceError && <div className="px-5 py-3 text-[12px]" style={{ color: MI6_RED }}>Could not load cadence queue: {cadenceError}</div>}
                  {!cadenceError && cadenceQueue.length === 0 ? (
                    <div className="px-5 py-6 text-[13px]" style={{ color: SLATE }}>Your cadence queue is clear. Pull new prospects from the hunt list to enroll.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full" style={{ borderCollapse: "collapse" }}>
                        <thead>
                          <tr className="border-b" style={{ borderColor: BORDER }}>
                            {["Prospect", "Step / Action", "Due", "Mark done"].map((h) => (
                              <th key={h} className="px-4 py-2 text-left text-[9px] uppercase tracking-[0.14em] font-bold" style={{ color: SLATE }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {cadenceQueue.map((item) => (
                            <LiveQueueRow key={item.enrollmentId} item={item}
                              onMarkDone={async (enrollmentId, outcome, notes) => { await markStepDone({ enrollmentId, outcome, notes }); }}
                              isCompleting={completing === item.enrollmentId} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* SECONDARY: Hunt list (unenrolled prospects) */}
                {huntQueue.length > 0 && (
                  <div id="hunt-list" className="rounded-xl overflow-hidden" style={{ background: "#FFFFFF", border: `1px solid ${BORDER}` }}>
                    <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: BORDER }}>
                      <div>
                        <div className="text-[9px] uppercase tracking-[0.2em] font-bold" style={{ color: SLATE }}>Hunt list (unenrolled prospects)</div>
                        <div className="text-base font-bold mt-0.5" style={{ color: MI6_BLACK }}>{huntQueue.length} suggested touch{huntQueue.length !== 1 ? "es" : ""}</div>
                      </div>
                      <Link href="/cadence/bdr" className="text-[11px] font-bold uppercase tracking-wide hover:opacity-70" style={{ color: MI6_RED }}>Enroll →</Link>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full" style={{ borderCollapse: "collapse" }}>
                        <thead>
                          <tr className="border-b" style={{ borderColor: BORDER }}>
                            {["Contact", "Account", "Step", "Due", "Health", "Action", "Log"].map((h) => (
                              <th key={h} className="px-4 py-2 text-left text-[9px] uppercase tracking-[0.14em] font-bold" style={{ color: SLATE }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {displayHuntQueue.map((row) => (
                            <QueueRow key={row.id} row={row} onEscalationOpen={setEscalationRow} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {hasMoreHunt && (
                      <div className="px-5 py-3 border-t text-right" style={{ borderColor: BORDER }}>
                        <button type="button" className="text-[11px] font-medium hover:opacity-70" style={{ color: SLATE }} onClick={() => setShowMoreHunt(true)}>
                          Show {huntQueue.length - 10} more
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <ConversionRatioStrip data={STUB_CONVERSION} />
                <ManagerCoachingSection repUserId={repUserId} />
              </div>

              <div className="w-72 flex-shrink-0 space-y-4">
                <CadenceHealthPanel data={cadenceHealth} />
                <ActivityTicker items={activityTicker} />
              </div>
            </div>
          </div>

          <div className="w-full py-3 px-6 flex items-center justify-between" style={{ background: MI6_BLACK }}>
            <span className="text-[11px]" style={{ color: "#9CA3AF" }}>© 2026 Mi6 Group LLC — Confidential. All intellectual property is the property of Mi6 Group LLC.</span>
            <span className="text-[11px]" style={{ color: "#9CA3AF" }}>Attilius · Revenue Lifecycle · {AIP_VERSION}</span>
          </div>

          {escalationRow && <MarketingEscalationModal row={escalationRow} onClose={() => setEscalationRow(null)} />}
        </div>
      )}
    </OperatorShell>
  );
}
